// Detailed load performance for one document, sent as `app_performance`.
//
// PostHog already captures `$web_vitals` (LCP, CLS, INP, FCP). This event adds
// what vitals don't carry — navigation timing breakdown, JS heap, network
// quality, resource weight and device class — so a slow load can be traced to
// the server, the network, the bundle or the device.
//
// Once per document, never per client-side route change: navigation timing
// and resource entries describe the document load, so re-reading them on an
// App Router transition would report the same load again under another path.

import type { AnalyticsEventMap } from "./events"

type AppPerformance = AnalyticsEventMap["app_performance"]
type Fields<K extends keyof AppPerformance> = Pick<AppPerformance, K>

/** Chrome/Edge only, and not in the DOM lib types. */
interface PerformanceMemory {
  usedJSHeapSize: number
  totalJSHeapSize: number
  jsHeapSizeLimit: number
}

/** Network Information API: Chromium only, not in the DOM lib types. */
interface NetworkInformation {
  effectiveType?: string
  downlink?: number
  rtt?: number
  saveData?: boolean
}

const BYTES_PER_MB = 1_048_576
const STYLESHEET_PATTERN = /\.css(?:[?#]|$)/i

const ms = (value: number) => Math.max(0, Math.round(value))
const mb = (bytes: number) => Math.round((bytes / BYTES_PER_MB) * 100) / 100

function navigationMetrics(
  nav: PerformanceNavigationTiming | undefined
): Partial<
  Fields<
    | "perf_nav_type"
    | "perf_page_load_ms"
    | "perf_ttfb_ms"
    | "perf_server_response_ms"
    | "perf_dns_ms"
    | "perf_tcp_connect_ms"
    | "perf_response_ms"
    | "perf_dom_processing_ms"
    | "perf_transfer_size_bytes"
    | "perf_encoded_size_bytes"
    | "perf_decoded_size_bytes"
  >
> {
  if (!nav) return {}
  return {
    perf_nav_type: nav.type,
    perf_page_load_ms: ms(nav.loadEventEnd - nav.startTime),
    // Web-vitals definition: from navigation start, so redirects, DNS, TCP
    // and TLS are included. `perf_server_response_ms` isolates the server.
    perf_ttfb_ms: ms(nav.responseStart - nav.startTime),
    perf_server_response_ms: ms(nav.responseStart - nav.requestStart),
    perf_dns_ms: ms(nav.domainLookupEnd - nav.domainLookupStart),
    perf_tcp_connect_ms: ms(nav.connectEnd - nav.connectStart),
    perf_response_ms: ms(nav.responseEnd - nav.responseStart),
    perf_dom_processing_ms: ms(nav.domComplete - nav.domInteractive),
    perf_transfer_size_bytes: nav.transferSize,
    perf_encoded_size_bytes: nav.encodedBodySize,
    perf_decoded_size_bytes: nav.decodedBodySize,
  }
}

function memoryMetrics(): Fields<
  | "perf_js_heap_used_mb"
  | "perf_js_heap_total_mb"
  | "perf_js_heap_limit_mb"
  | "perf_js_heap_usage_pct"
> {
  const memory = (performance as Performance & { memory?: PerformanceMemory })
    .memory
  if (!memory || !memory.jsHeapSizeLimit) {
    return {
      perf_js_heap_used_mb: null,
      perf_js_heap_total_mb: null,
      perf_js_heap_limit_mb: null,
      perf_js_heap_usage_pct: null,
    }
  }
  return {
    perf_js_heap_used_mb: mb(memory.usedJSHeapSize),
    perf_js_heap_total_mb: mb(memory.totalJSHeapSize),
    perf_js_heap_limit_mb: mb(memory.jsHeapSizeLimit),
    perf_js_heap_usage_pct: Math.round(
      (memory.usedJSHeapSize / memory.jsHeapSizeLimit) * 100
    ),
  }
}

function networkMetrics(): Fields<
  | "perf_connection_type"
  | "perf_downlink_mbps"
  | "perf_rtt_ms"
  | "perf_save_data"
> {
  const connection = (
    navigator as Navigator & { connection?: NetworkInformation }
  ).connection
  return {
    perf_connection_type: connection?.effectiveType ?? null,
    perf_downlink_mbps: connection?.downlink ?? null,
    perf_rtt_ms: connection?.rtt ?? null,
    perf_save_data: connection?.saveData ?? null,
  }
}

function resourceMetrics(
  resources: PerformanceResourceTiming[]
): Fields<
  | "perf_resource_count"
  | "perf_resource_total_kb"
  | "perf_script_count"
  | "perf_image_count"
  | "perf_css_count"
  | "perf_fetch_count"
> {
  let transferBytes = 0
  let scripts = 0
  let images = 0
  let stylesheets = 0
  let fetches = 0
  for (const resource of resources) {
    // Zero for cache hits and for cross-origin responses without
    // `Timing-Allow-Origin`: the total is a floor, not the real weight.
    transferBytes += resource.transferSize || 0
    if (resource.initiatorType === "script") scripts++
    else if (resource.initiatorType === "img") images++
    else if (
      resource.initiatorType === "fetch" ||
      resource.initiatorType === "xmlhttprequest"
    )
      fetches++
    // `link` also covers preloads, prefetches and fonts; the URL is what
    // tells a stylesheet apart.
    if (STYLESHEET_PATTERN.test(resource.name)) stylesheets++
  }
  return {
    perf_resource_count: resources.length,
    perf_resource_total_kb: Math.round(transferBytes / 1024),
    perf_script_count: scripts,
    perf_image_count: images,
    perf_css_count: stylesheets,
    perf_fetch_count: fetches,
  }
}

function paintMetrics(): Fields<"perf_fp_ms" | "perf_fcp_ms"> {
  const paints = performance.getEntriesByType("paint")
  const at = (name: string) => {
    const entry = paints.find((paint) => paint.name === name)
    return entry ? ms(entry.startTime) : null
  }
  return {
    perf_fp_ms: at("first-paint"),
    perf_fcp_ms: at("first-contentful-paint"),
  }
}

export function collectPerformanceMetrics(): AppPerformance {
  const [nav] = performance.getEntriesByType(
    "navigation"
  ) as PerformanceNavigationTiming[]
  const resources = performance.getEntriesByType(
    "resource"
  ) as PerformanceResourceTiming[]
  const device = navigator as Navigator & { deviceMemory?: number }

  return {
    perf_context: "page_load",
    perf_page: window.location.pathname,
    ...navigationMetrics(nav),
    ...memoryMetrics(),
    ...networkMetrics(),
    ...resourceMetrics(resources),
    ...paintMetrics(),
    perf_viewport_width: window.innerWidth,
    perf_viewport_height: window.innerHeight,
    perf_device_pixel_ratio: window.devicePixelRatio,
    perf_online: navigator.onLine,
    perf_hardware_concurrency: navigator.hardwareConcurrency ?? null,
    perf_device_memory_gb: device.deviceMemory ?? null,
  }
}

/**
 * Sends `app_performance` once this document has finished loading.
 *
 * `loadEventEnd` is still 0 inside the `load` listener itself, so the read is
 * pushed to the next task. Safe to call more than once: the event goes out a
 * single time per document.
 */
let scheduled = false

export function reportPerformanceOnLoad(
  send: (properties: AppPerformance) => void
): void {
  if (scheduled || typeof window === "undefined" || !window.performance) return
  scheduled = true

  const run = () => setTimeout(() => send(collectPerformanceMetrics()), 0)

  if (document.readyState === "complete") run()
  else window.addEventListener("load", run, { once: true })
}
